import React, { useState } from 'react';
import { auth, googleProvider } from '../services/firebase';
import { LogoIcon } from './icons';
import LegalModal from './LegalModal';
import { PrivacyPolicyContent, TermsContent } from './legalContent';
import SecurityFeatures from './SecurityFeatures';

const LoginView: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLogin, setIsLogin] = useState(true);
    const [isResetMode, setIsResetMode] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);
    const [legalModalContent, setLegalModalContent] = useState<{ title: string; content: React.ReactNode } | null>(null);

    const handleOpenLegalModal = (type: 'privacy' | 'terms') => {
        switch (type) {
            case 'privacy':
                setLegalModalContent({ title: "Política de Privacidad", content: <PrivacyPolicyContent /> });
                break;
            case 'terms':
                setLegalModalContent({ title: "Términos de Uso", content: <TermsContent /> });
                break;
        }
        setIsLegalModalOpen(true);
    };

    const handleAuthAction = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        if (!auth) {
            setError("Error: El servicio de autenticación no está disponible.");
            setLoading(false);
            return;
        }

        if (!isResetMode) {
            if (!isLogin) {
                // New account: enforce strong password policy
                if (password.length < 12) {
                    setError("La contraseña debe tener al menos 12 caracteres.");
                    setLoading(false);
                    return;
                }
                if (!/[A-Z]/.test(password)) {
                    setError("La contraseña debe contener al menos una letra mayúscula.");
                    setLoading(false);
                    return;
                }
                if (!/[0-9]/.test(password)) {
                    setError("La contraseña debe contener al menos un número.");
                    setLoading(false);
                    return;
                }
            } else {
                // Login: backward compatibility with existing accounts
                if (password.length < 6) {
                    setError("La contraseña debe tener al menos 6 caracteres.");
                    setLoading(false);
                    return;
                }
            }
        }

        try {
            if (isLogin) {
                await auth.signInWithEmailAndPassword(email, password);
                // App.tsx will now handle the redirection or verification check
            } else {
                 if (!termsAccepted) {
                    setError("Debes aceptar los términos y la política de privacidad para registrarte.");
                    setLoading(false);
                    return;
                }
                const userCredential = await auth.createUserWithEmailAndPassword(email, password);
                await userCredential.user.sendEmailVerification();
                await auth.signOut(); // Sign out user until they verify
                setSuccess('¡Cuenta creada! Te hemos enviado un correo para verificar tu cuenta. Revisa tu bandeja de entrada y la carpeta de spam. Si el correo está en spam, márcalo como "No es spam" para poder hacer clic en el enlace.');
                setIsLogin(true); // Switch to login view
            }
        } catch (err: any) {
             console.error("Firebase Auth Error:", err);
             switch (err.code) {
                case 'auth/invalid-email':
                    setError("El formato del email no es válido.");
                    break;
                case 'auth/user-not-found':
                case 'auth/wrong-password':
                case 'auth/invalid-credential':
                     setError("El email o la contraseña son incorrectos.");
                    break;
                case 'auth/email-already-in-use':
                    setError("Este email ya está registrado. Intenta iniciar sesión.");
                    break;
                default:
                    setError("Ha ocurrido un error. Por favor, inténtalo de nuevo.");
                    break;
            }
        } finally {
            setLoading(false);
        }
    };
    
    const handleGoogleSignIn = async () => {
        if (!auth) {
            setError("Error: El servicio de autenticación no está disponible.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await auth.signInWithPopup(googleProvider);
        } catch (err: any) {
            if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
                setError("Error al iniciar sesión con Google. Inténtalo de nuevo.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);
        
        if (!auth) {
            setError("Error: El servicio de autenticación no está disponible.");
            setLoading(false);
            return;
        }

        try {
            await auth.sendPasswordResetEmail(email);
            setSuccess("¡Hecho! Revisa tu correo electrónico para restablecer la contraseña.");
        } catch (err: any) {
            console.error("Firebase Reset Error:", err);
            switch (err.code) {
                case 'auth/invalid-email':
                    setError("El formato del email no es válido.");
                    break;
                case 'auth/user-not-found':
                     setError("No se ha encontrado ningún usuario con este email.");
                    break;
                default:
                    setError("Ha ocurrido un error al enviar el correo.");
                    break;
            }
        } finally {
            setLoading(false);
        }
    };


    return (
        <>
        <LegalModal
            isOpen={isLegalModalOpen}
            onClose={() => setIsLegalModalOpen(false)}
            title={legalModalContent?.title || ''}
        >
            {legalModalContent?.content}
        </LegalModal>
        <div className="flex flex-col items-center justify-center min-h-screen bg-light-background dark:bg-background p-4">
            <div className="w-full max-w-4xl bg-light-surface dark:bg-surface rounded-xl shadow-2xl border border-light-border dark:border-border overflow-hidden grid md:grid-cols-2">
                <div className="hidden md:flex flex-col items-center justify-center p-12 bg-gray-100 dark:bg-gray-800 text-center">
                    <LogoIcon className="w-24 h-24 text-primary mb-6" />
                    <h1 className="text-3xl font-bold text-light-text-primary dark:text-text-primary">Bienvenido a Mis Cuentas 360</h1>
                    <p className="mt-4 text-light-text-secondary dark:text-text-secondary">Tu centro de control financiero. Unifica tus datos, analiza tu rendimiento y toma decisiones más acertadas.</p>
                </div>

                <div className="p-8 sm:p-12">
                     <div className="md:hidden text-center mb-8">
                        <LogoIcon className="w-20 h-20 text-primary mx-auto mb-4" />
                        <h1 className="text-3xl font-bold text-light-text-primary dark:text-text-primary">
                            Bienvenido a Mis Cuentas 360
                        </h1>
                    </div>
                     <h2 className="text-3xl font-bold text-center text-light-text-primary dark:text-text-primary mb-6">
                        {isResetMode ? 'Recuperar Contraseña' : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta')}
                    </h2>
                    
                    {success && <p className="text-sm text-green-400 text-center mb-4 bg-green-500/10 p-3 rounded-lg">{success}</p>}

                    {isResetMode ? (
                         <form className="space-y-6" onSubmit={handlePasswordReset}>
                             <p className="text-center text-sm text-light-text-secondary dark:text-text-secondary">Introduce tu email y te enviaremos un enlace para que puedas recuperar tu cuenta.</p>
                             <input id="email-address" name="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)}
                                className="appearance-none relative block w-full px-3 py-3 border border-light-border dark:border-border bg-light-background dark:bg-background placeholder-gray-500 text-light-text-primary dark:text-text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm" placeholder="Correo Electrónico"/>
                             {error && <p className="text-sm text-red-400 text-center">{error}</p>}
                             
                             <button type="submit" disabled={loading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:bg-gray-600 transition-colors">
                                 {loading ? 'Enviando...' : 'Enviar Enlace de Recuperación'}
                             </button>
                         </form>
                    ) : (
                        <form className="space-y-6" onSubmit={handleAuthAction} noValidate>
                            <input id="email-address" name="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)}
                                className="appearance-none relative block w-full px-3 py-3 border border-light-border dark:border-border bg-light-background dark:bg-background placeholder-gray-500 text-light-text-primary dark:text-text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm" placeholder="Correo Electrónico"/>
                            <input id="password" name="password" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)}
                                className="appearance-none relative block w-full px-3 py-3 border border-light-border dark:border-border bg-light-background dark:bg-background placeholder-gray-500 text-light-text-primary dark:text-text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm" placeholder="Contraseña"/>
                            
                            {!isLogin && (
                                <div className="flex items-start">
                                    <div className="flex items-center h-5">
                                        <input
                                            id="terms"
                                            name="terms"
                                            type="checkbox"
                                            checked={termsAccepted}
                                            onChange={(e) => setTermsAccepted(e.target.checked)}
                                            className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="terms" className="text-light-text-secondary dark:text-text-secondary">
                                            Acepto los{' '}
                                            <button type="button" onClick={() => handleOpenLegalModal('terms')} className="font-medium text-primary hover:underline">Términos de Uso</button>
                                            {' '}y la{' '}
                                            <button type="button" onClick={() => handleOpenLegalModal('privacy')} className="font-medium text-primary hover:underline">Política de Privacidad</button>.
                                        </label>
                                    </div>
                                </div>
                            )}

                            {error && <p className="text-sm text-red-400 text-center">{error}</p>}
                            <button type="submit" disabled={loading || (!isLogin && !termsAccepted)} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:bg-gray-500 disabled:cursor-not-allowed transition-colors">
                                {loading ? 'Procesando...' : (isLogin ? 'Iniciar Sesión' : 'Registrarse')}
                            </button>

                            <div className="relative my-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-light-border dark:border-border"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-4 bg-light-surface dark:bg-surface text-light-text-secondary dark:text-text-secondary">o continúa con</span>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={handleGoogleSignIn}
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-light-border dark:border-border rounded-md bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-light-text-primary dark:text-text-primary font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                Iniciar sesión con Google
                            </button>
                        </form>
                    )}

                    <div className="mt-6 text-sm text-center">
                        {isResetMode ? (
                            <button onClick={() => { setIsResetMode(false); setError(null); setSuccess(null); }} className="font-medium text-primary hover:text-blue-400 focus:outline-none">
                                Volver a Iniciar Sesión
                            </button>
                        ) : isLogin ? (
                            <>
                                <button onClick={() => { setIsLogin(false); setError(null); setSuccess(null); }} className="font-medium text-primary hover:text-blue-400 focus:outline-none">
                                    ¿No tienes cuenta? Regístrate
                                </button>
                                <button onClick={() => { setIsResetMode(true); setError(null); setSuccess(null); }} className="font-medium text-primary hover:text-blue-400 focus:outline-none ml-4">
                                    ¿Olvidaste la contraseña?
                                </button>
                            </>
                        ) : (
                            <button onClick={() => { setIsLogin(true); setError(null); setSuccess(null); }} className="font-medium text-primary hover:text-blue-400 focus:outline-none">
                                ¿Ya tienes cuenta? Inicia Sesión
                            </button>
                        )}
                    </div>
                </div>
            </div>
             <div className="w-full max-w-4xl mt-8">
                <SecurityFeatures />
            </div>
        </div>
        </>
    );
};

export default LoginView;